<script setup lang="ts">
import {Modal} from "bootstrap"
import {computed, nextTick, onBeforeUnmount, ref, useTemplateRef} from "vue";

const props = defineProps({
  modalTitle: {type: String, default: ""},
  modalClass: {type: String, default: ""},
  modalDialogClass: {type: String, default: ""},
  keyboard: {type: Boolean, default: true},
})

const modal = useTemplateRef<HTMLDivElement>("modal")

const modalVisible = ref(false)
const modalEventsAttached = ref(false)
// true from show/hide start until shown/hidden, bootstrap must not be disposed in between
let modalTransitioning = false

const emit = defineEmits<{
  (e: 'modal-hidden'): void
  (e: 'modal-shown'): void
  (e: 'modal-show'): void
}>()

const show = () => {
  if (!modalEventsAttached.value) {
    const modalEl = modal.value
    if (modalEl) {
      modalEventsAttached.value = true

      // Bootstrap events bubble, only handle the events of this modal
      modalEl.addEventListener('hidden.bs.modal', (e) => {
        if (e.target !== modalEl) {
          return
        }
        modalTransitioning = false
        modalVisible.value = false
        emit('modal-hidden')
      })

      modalEl.addEventListener('show.bs.modal', (e) => {
        if (e.target === modalEl) {
          modalTransitioning = true
          emit('modal-show')
        }
      })

      modalEl.addEventListener('shown.bs.modal', (e) => {
        if (e.target === modalEl) {
          modalTransitioning = false
          // when stacked on another modal, the focus trap of the modal below moves the focus
          // into that modal while this one is activated (Escape would then close the modal below)
          if (!modalEl.contains(document.activeElement)) {
            modalEl.focus()
          }
          emit('modal-shown')
        }
      })

      modalEl.addEventListener('hide.bs.modal', (e) => {
        if (e.target === modalEl) {
          modalTransitioning = true
        }
      })
    }
  }

  if (!modalVisible.value) {
    modalVisible.value = true

    // show modal on next render
    nextTick(() => {
      const modalEl = modal.value
      if (modalEl) {
        // show modal, the config is used when the instance is created
        Modal.getOrCreateInstance(modalEl, {
          keyboard: props.keyboard,
          backdrop: 'static',
        }).show()
      }
    })
  }
}

const hide = () => {
  const modalEl = modal.value
  if (modalEl) {
    // do not create the instance here, it would be created with the default config
    Modal.getInstance(modalEl)?.hide()
  }
}

const isOpen = computed((): boolean => modalVisible.value)

onBeforeUnmount(() => {
  const modalEl = modal.value
  if (!modalEl) {
    return
  }

  const disposeModal = () => Modal.getInstance(modalEl)?.dispose()
  if (modalTransitioning) {
    // pending bootstrap transition callbacks fail on a disposed instance
    modalEl.addEventListener('shown.bs.modal', disposeModal, {once: true})
    modalEl.addEventListener('hidden.bs.modal', disposeModal, {once: true})
  } else {
    disposeModal()
  }
})

defineExpose({
  hide,
  isOpen,
  show,
  get modalEl(): HTMLDivElement | null {
    return modal.value
  }
})
</script>

<template>
  <div
    ref="modal"
    :class="['modal fade', modalClass]"
    tabindex="-1"
    role="dialog"
    aria-labelledby="modalLabel"
    aria-hidden="true"
  >
    <div
      :class="['modal-dialog', modalDialogClass]"
      role="document"
    >
      <div class="modal-content">
        <div class="modal-header">
          <h5
            id="modalLabel"
            class="modal-title"
          >
            {{ modalTitle }}
          </h5>
          <button
            type="button"
            class="btn-close"
            data-bs-dismiss="modal"
            aria-label="Close"
          />
        </div>
        <slot name="modal-body">
          <div class="modal-body">
            <slot name="modal-content" />
          </div>
        </slot>
        <div class="modal-footer">
          <slot name="modal-footer">
            <button
              type="button"
              class="btn btn-sm btn-secondary"
              data-bs-dismiss="modal"
            >
              Close
            </button>
          </slot>
        </div>
        <slot name="modal-footer-after"/>
      </div>
    </div>
  </div>
</template>
